import { useCallback } from 'react'

import { useQueryClient } from '@tanstack/react-query'

import { QUERY_KEYS } from '~/constants/react-query'
import { useActiveOrgId } from '~/hooks/org'
import { passkeyConfirmationHeaders } from '~/hooks/usePasskeyConfirmation'
import { useServerRequests } from '~/hooks/useRequests'
import { OrgSmartAccount } from '~/types'

export const useArchiveSmartAccount = (smartAccount: OrgSmartAccount) => {
  const { serverPutRequest } = useServerRequests()
  const queryClient = useQueryClient()
  const activeOrgId = useActiveOrgId()

  const archiveSmartAccount = useCallback(
    async ({
      confirmationId,
      onSuccess,
    }: {
      confirmationId: string
      onSuccess?: () => void
    }) => {
      const result = await serverPutRequest({
        path: '/api/smart_accounts/edit/archive',
        params: { smartAccountId: smartAccount.id },
        headers: passkeyConfirmationHeaders(confirmationId),
        successMessage: 'Account successfully archived',
        errorMessage: 'Failed to archive account',
      })
      if (!result) return

      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.TRANSACTIONS],
        predicate: (query) => {
          return (
            Array.isArray(query.queryKey) &&
            query.queryKey[1]?.orgId === activeOrgId
          )
        },
      })
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.SMART_ACCOUNT, activeOrgId, smartAccount.address],
      })
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.ORG_SMART_ACCOUNTS, activeOrgId],
      })

      onSuccess?.()
    },
    [
      serverPutRequest,
      smartAccount.id,
      smartAccount.address,
      queryClient,
      activeOrgId,
    ],
  )

  const unarchiveSmartAccount = useCallback(
    async ({
      confirmationId,
      onSuccess,
    }: {
      confirmationId: string
      onSuccess?: () => void
    }) => {
      const result = await serverPutRequest({
        path: '/api/smart_accounts/edit/unarchive',
        params: { smartAccountId: smartAccount.id },
        headers: passkeyConfirmationHeaders(confirmationId),
        successMessage: 'Account successfully restored',
        errorMessage: 'Failed to restore account',
      })
      if (!result) return

      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.ORG_SMART_ACCOUNTS, activeOrgId],
      })
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.SMART_ACCOUNT, activeOrgId, smartAccount.address],
      })

      onSuccess?.()
    },
    [
      serverPutRequest,
      smartAccount.id,
      smartAccount.address,
      queryClient,
      activeOrgId,
    ],
  )

  return { archiveSmartAccount, unarchiveSmartAccount }
}
